<script lang="ts">
  import { navigate } from '../router.svelte';
  import type { TopContent, PostBrief, CommentBrief, PostAuthor } from '../admin';
  import PeriodSelector from './PeriodSelector.svelte';
  import Avatar from '@ui/Avatar.svelte';
  import MemberHover from '@ui/MemberHover.svelte';

  // The "Top content" group: most upvoted / most discussed posts and most upvoted /
  // most replied-to comments, under one titled box with a Period control that drives
  // their look-back — mirrors the "Contributors & Engagement" group. Members-only.
  // Each row carries the author avatar (with hover summary) and links to the post.
  let {
    data,
    days = $bindable(),
    class: cls = '',
  }: { data: TopContent; days: number; class?: string } = $props();

  type Row = {
    key: number;
    author: PostAuthor | null;
    href: string;
    badge: string;
    tone: string;
    primary: string;
    secondary: string;
  };

  const toneClass: Record<string, string> = {
    primary: 'bg-primary/10 text-primary',
    secondary: 'bg-secondary/10 text-secondary',
    success: 'bg-success/10 text-success',
    info: 'bg-info/10 text-info',
  };
  const postHref = (id: number): string => `/admin/community/timeline?post=${id}`;

  const postRow = (p: PostBrief, mode: 'up' | 'disc'): Row => ({
    key: p.id,
    author: p.author,
    href: postHref(p.id),
    badge: mode === 'up' ? `▲${p.upvotes ?? 0}` : `💬${p.comment_count ?? 0}`,
    tone: mode === 'up' ? 'primary' : 'secondary',
    primary: p.title || '(untitled)',
    secondary: `${p.author?.name ?? 'Unknown'} · ${mode === 'up' ? `💬${p.comment_count ?? 0}` : `▲${p.upvotes ?? 0}`}`,
  });

  const commentRow = (c: CommentBrief, mode: 'up' | 'rep'): Row => ({
    key: c.id,
    author: c.author,
    href: postHref(c.post_id),
    badge: mode === 'up' ? `▲${c.upvotes ?? 0}` : `↩${c.replies}`,
    tone: mode === 'up' ? 'success' : 'info',
    primary: c.excerpt || '(no text)',
    secondary: `${c.author?.name ?? 'Unknown'} · on ${c.post_title ?? 'a post'}`,
  });

  const lists = $derived([
    { title: 'Most upvoted posts', rows: data.top_posts_by_upvotes.map((p) => postRow(p, 'up')) },
    { title: 'Most discussed posts', rows: data.top_posts_by_comments.map((p) => postRow(p, 'disc')) },
    { title: 'Most upvoted comments', rows: data.top_comments_by_upvotes.map((c) => commentRow(c, 'up')) },
    { title: 'Most replied-to comments', rows: data.top_comments_by_replies.map((c) => commentRow(c, 'rep')) },
  ]);
</script>

<section class="rounded-box border hair bg-base-100 p-5 {cls}">
  <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
    <div class="eyebrow text-base-content/70">Top content</div>
    <PeriodSelector bind:days note="look-back" />
  </div>
  <div class="grid gap-x-6 gap-y-6 sm:grid-cols-2">
    {#each lists as list (list.title)}
      <div>
        <!-- privacy-ok: the widget's own heading ("Top posts"), not a post's title -->
  <div class="eyebrow mb-2">{list.title}</div>
        {#if list.rows.length === 0}
          <p class="text-sm text-base-content/62">Nothing in this window.</p>
        {:else}
          <ul>
            {#each list.rows as r (r.key)}
              <li class="flex items-center gap-2.5 rounded-field px-1.5 py-1.5 transition-colors hover:bg-base-200/60">
                {#if r.author}
                  <MemberHover memberId={r.author.member_id} class="shrink-0">
                    <Avatar src={r.author.picture_profile} name={r.author.name} size={30} memberId={r.author.member_id} />
                  </MemberHover>
                {:else}
                  <span class="shrink-0"><Avatar name="?" size={30} /></span>
                {/if}
                <a
                  class="min-w-0 flex-1"
                  href={r.href}
                  onclick={(e) => { e.preventDefault(); navigate(r.href); }}
                >
                  <span class="block truncate text-sm transition-colors hover:text-primary">{r.primary}</span>
                  <span class="block truncate font-mono text-[0.7rem] text-base-content/65">{r.secondary}</span>
                </a>
                <span class="shrink-0 rounded px-1.5 py-0.5 font-mono text-[0.7rem] font-medium tabular-nums {toneClass[r.tone]}">{r.badge}</span>
              </li>
            {/each}
          </ul>
        {/if}
      </div>
    {/each}
  </div>
</section>
